// src/components/checkout.jsx
// Full checkout page with plan selection (Monthly + Yearly).
// Monnify for NGN payments. Stripe for international cards.
// Writes subscriptionTier + subscriptionEndDate for useSubscription.js to read.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  doc, updateDoc, serverTimestamp, Timestamp, addDoc, collection
} from 'firebase/firestore';
import { db } from '../firebase';
import { PRICING, formatNaira } from '../hooks/useSubscription';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_key');

const GOLD = '#B8860B';
const NAVY = '#001F3F';
const LOGO_URL = '/icons/esdras-logo.png';

// ── Plan selection component ──────────────────────────────────────────────────
function PlanSelector({ selected, onSelect }) {
  return (
    <div style={planGrid}>
      {Object.entries(PRICING).map(([key, plan]) => (
        <button
          key={key}
          onClick={() => onSelect(key)}
          style={{
            ...planBtn,
            border: selected === key ? `2px solid ${GOLD}` : '2px solid rgba(255,255,255,0.1)',
            background: selected === key ? 'rgba(184,134,11,0.12)' : 'rgba(255,255,255,0.03)',
          }}
        >
          {plan.badge && <div style={planBadge}>{plan.badge}</div>}
          <div style={planLabel}>{plan.label}</div>
          <div style={planPrice}>{formatNaira(plan.price)}</div>
          <div style={planMeta}>✓ {plan.scansPerMonth} scans/month</div>
          <div style={planMeta}>✓ Unlimited style try-ons</div>
          <div style={{ ...planMeta, opacity: 0.45, marginTop: '4px' }}>{plan.description}</div>
        </button>
      ))}
    </div>
  );
}

// ── Inner form (must be inside <Elements>) ────────────────────────────────────
const CheckoutForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;
  const functions = getFunctions();

  const [selectedTier, setSelectedTier] = useState('yearly');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [successData, setSuccessData] = useState(null);

  const plan = PRICING[selectedTier];
  const amount = plan.price;

  // ── Activate subscription in Firestore ──────────────────────────────────────
  const activateSubscription = async (txId, method) => {
    try {
      const now = new Date();
      const expiryDate = new Date(now);
      if (selectedTier === 'monthly') {
        expiryDate.setMonth(expiryDate.getMonth() + 1);
      } else {
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
      }

      await updateDoc(doc(db, 'users', user.uid), {
        // Canonical fields
        subscriptionTier: selectedTier,
        subscriptionStatus: 'active',
        subscriptionEndDate: Timestamp.fromDate(expiryDate),
        subscriptionStartDate: serverTimestamp(),
        monthlyScansUsed: 0,
        monthlyScansResetDate: serverTimestamp(),

        // Legacy fields for backward compatibility
        isPremium: true,
        isSubscribed: true,
        premiumSince: serverTimestamp(),
        subscriptionExpires: Timestamp.fromDate(expiryDate),
        scanCount: 0,
        scansUsed: 0,
        tryOnCount: 0,
        role: 'premium',
        lastTransactionId: txId,
        paymentMethod: method,
        updatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, 'transactions'), {
        userId: user.uid,
        userName: user.displayName || user.email,
        amount,
        type: `subscription_${selectedTier}`,
        plan: plan.label,
        scansPerMonth: plan.scansPerMonth,
        method,
        reference: txId,
        status: 'success',
        createdAt: serverTimestamp(),
      });

      // Send receipt email (optional)
      try {
        const sendReceipt = httpsCallable(functions, 'sendBookingNotifications');
        await sendReceipt({
          clientEmail: user.email,
          stylistName: 'Esdras Premium System',
          amount,
          totalPrice: amount,
        });
      } catch (e) {
        console.warn('Receipt email skipped:', e.message);
      }

      setSuccessData({ 
        txId, 
        method, 
        expiry: expiryDate.toLocaleDateString(), 
        plan: plan.label 
      });
    } catch (err) {
      console.error('Activation Error:', err);
      setError('Payment received, but account update failed. Ref: ' + txId);
    }
  };

  const downloadReceipt = (data) => {
    const content = `
======================================
         ESDRAS TECHNOLOGIES
           OFFICIAL RECEIPT
======================================

Transaction ID : ${data.txId}
Date : ${new Date().toLocaleString()}
Customer : ${user.email}
Plan : ESDRAS SHIELD (${data.plan})
Amount : ${formatNaira(amount)}
Status : PAID via ${data.method.toUpperCase()}
Expiry Date : ${data.expiry}

--------------------------------------
Thank you for securing your style with Esdras.
Keep this for your records.
======================================
    `.trim();

    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([content], { type: 'text/plain' }));
    a.download = `Esdras_Receipt_${data.txId}.txt`;
    a.click();
  };

  // ── Monnify (NGN) ───────────────────────────────────────────────────────────
  const handleMonnify = () => {
    if (!user) return setError('Please log in to continue.');
    if (!window.MonnifySDK) return setError('Payment gateway not loaded. Please refresh.');

    setProcessing(true);

    window.MonnifySDK.initialize({
      amount,
      currency: 'NGN',
      reference: `ESD_SUB_${selectedTier.toUpperCase()}_${Date.now()}`,
      customerFullName: user.displayName || user.email.split('@')[0],
      customerEmail: user.email,
      apiKey: process.env.REACT_APP_MONNIFY_API_KEY,
      contractCode: process.env.REACT_APP_MONNIFY_CONTRACT_CODE,
      paymentDescription: `Esdras Shield ${plan.label}`,
      metadata: { 
        userId: user.uid, 
        subscriptionTier: selectedTier, 
        type: 'subscription' 
      },
      onComplete: (response) => {
        setProcessing(false);
        if (response.status === 'SUCCESS') {
          activateSubscription(response.transactionReference, 'monnify');
        } else {
          setError('Payment was not completed. Please try again.');
        }
      },
      onClose: () => setProcessing(false),
    });
  };

  // ── Stripe (international) ──────────────────────────────────────────────────
  const handleStripe = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !user) return;

    setProcessing(true);
    setError(null);

    try {
      const createPI = httpsCallable(functions, 'createPaymentIntent');
      const result = await createPI({ 
        amount: amount * 100, 
        currency: 'ngn', 
        userId: user.uid 
      });

      const payload = await stripe.confirmCardPayment(result.data.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { email: user.email },
        },
      });

      if (payload.error) throw new Error(payload.error.message);

      if (payload.paymentIntent.status === 'succeeded') {
        await activateSubscription(payload.paymentIntent.id, 'stripe');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // ── Success screen ──────────────────────────────────────────────────────────
  if (successData) {
    return (
      <div style={formStyle}>
        <div style={{ textAlign: 'center' }}>
          <img src={LOGO_URL} alt="ESDRAS" style={{ width: '80px', marginBottom: '10px' }} />
          <div style={{ fontSize: '3rem', marginBottom: '10px' }}>👑</div>
          <h1 style={{ color: GOLD, fontWeight: '900', letterSpacing: '2px' }}>SHIELD ACTIVATED</h1>
          <p style={{ opacity: 0.7, marginBottom: '8px' }}>{successData.plan} plan — active until {successData.expiry}</p>
          <p style={{ opacity: 0.5, fontSize: '0.75rem', marginBottom: '24px' }}>
            {PRICING[selectedTier].scansPerMonth} scans/month · Unlimited style try-ons
          </p>
          <button onClick={() => downloadReceipt(successData)} style={outlineBtn}>DOWNLOAD RECEIPT (.TXT)</button>
          <button onClick={() => navigate('/dashboard')} style={payBtn}>GO TO DASHBOARD</button>
        </div>
      </div>
    );
  }

  return (
    <div style={formStyle}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '28px' }}>
        <img src={LOGO_URL} alt="ESDRAS" style={{ width: '60px', marginBottom: '12px' }} />
        <h1 style={{ color: GOLD, fontSize: '1.8rem', fontWeight: '900', letterSpacing: '2px', margin: 0 }}>ESDRAS SHIELD</h1>
        <p style={{ opacity: 0.5, fontSize: '0.75rem', marginTop: '6px' }}>Choose your plan below</p>
      </div>

      {/* Plan selector */}
      <PlanSelector selected={selectedTier} onSelect={setSelectedTier} />

      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <span style={{ fontSize: '2rem', fontWeight: '900', color: 'white' }}>{formatNaira(amount)}</span>
        <span style={{ opacity: 0.5, fontSize: '0.8rem' }}>
          {selectedTier === 'monthly' ? ' / month' : ' / year'}
        </span>
      </div>

      {/* Monnify (primary for NGN) */}
      <button onClick={handleMonnify} disabled={processing} style={payBtn}>
        {processing ? 'INITIALIZING...' : `PAY ${formatNaira(amount)} VIA MONNIFY`}
      </button>

      <div style={divider}>— OR INTERNATIONAL CARD —</div>

      {/* Stripe (international) */}
      <div style={cardInputWrapper}>
        <CardElement options={{
          style: {
            base: { color: '#fff', fontSize: '16px', '::placeholder': { color: 'rgba(255,255,255,0.4)' } },
            invalid: { color: '#ff6b6b' },
          },
        }} />
      </div>

      {error && <p style={errorText}>{error}</p>}

      <button
        onClick={handleStripe}
        disabled={processing || !stripe}
        style={{ ...payBtn, background: 'transparent', color: GOLD, border: `1px solid ${GOLD}`, marginTop: '12px' }}
      >
        {processing ? 'VERIFYING...' : `PAY ${formatNaira(amount)} VIA STRIPE`}
      </button>

      <p style={footerNote}>
        Secure, encrypted processing via Monnify &amp; Stripe.<br />
        {selectedTier === 'monthly'
          ? 'Monthly plan renews every 30 days. Cancel anytime.'
          : 'Annual plan renews every 12 months. Cancel anytime.'}
      </p>
    </div>
  );
};

export default function Checkout() {
  return (
    <div style={pageContainer}>
      <Elements stripe={stripePromise}>
        <CheckoutForm />
      </Elements>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const pageContainer = { minHeight: '100vh', background: '#000', display: 'grid', placeItems: 'center', padding: '20px', fontFamily: 'Montserrat, sans-serif' };
const formStyle = { maxWidth: '460px', width: '100%', padding: '36px 28px', background: NAVY, borderRadius: '28px', border: `2px solid ${GOLD}`, boxShadow: '0 25px 50px rgba(0,0,0,0.6)', color: 'white' };
const planGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' };
const planBtn = { borderRadius: '16px', padding: '18px 10px', cursor: 'pointer', color: 'white', textAlign: 'center', position: 'relative', transition: 'all 0.2s' };
const planBadge = { position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', background: GOLD, color: '#000', fontSize: '0.5rem', fontWeight: '900', padding: '2px 10px', borderRadius: '10px', letterSpacing: '1px', whiteSpace: 'nowrap' };
const planLabel = { color: GOLD, fontWeight: '900', fontSize: '0.7rem', letterSpacing: '2px', marginBottom: '4px' };
const planPrice = { fontSize: '1.1rem', fontWeight: '900', color: 'white', marginBottom: '8px' };
const planMeta = { fontSize: '0.6rem', color: 'rgba(255,255,255,0.65)', marginBottom: '3px' };
const cardInputWrapper = { background: 'rgba(0,0,0,0.5)', padding: '18px', borderRadius: '12px', marginBottom: '8px', border: '1px solid rgba(184,134,11,0.3)' };
const payBtn = { width: '100%', background: GOLD, color: '#000', padding: '18px', border: 'none', borderRadius: '12px', fontWeight: '900', cursor: 'pointer', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' };
const outlineBtn = { width: '100%', background: 'transparent', border: `1px solid ${GOLD}`, color: GOLD, padding: '15px', borderRadius: '12px', marginBottom: '15px', cursor: 'pointer', fontWeight: 'bold' };
const divider = { margin: '20px 0', fontSize: '0.65rem', color: GOLD, textAlign: 'center', letterSpacing: '1px', fontWeight: 'bold' };
const errorText = { color: '#ff6b6b', textAlign: 'center', marginBottom: '10px', fontSize: '0.8rem', fontWeight: 'bold' };
const footerNote = { fontSize: '0.6rem', textAlign: 'center', marginTop: '20px', opacity: 0.4, color: '#fff', lineHeight: '1.4' };
